import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import { AmendmentBadge, formatAmendmentLabel } from './AmendmentBadge';
export function AmendmentTimeline({ entries, title, compact = false, className }) {
    if (entries.length === 0) return null;
    // Single-entry timelines collapse to a "no history" callout — there's
    // no story to tell when only one version exists. Caller usually filters
    // these out before mounting, but defensive rendering keeps the
    // component robust.
    if (entries.length === 1) {
        return /*#__PURE__*/ _jsx("div", {
            className: clsx('text-xs text-slate-500', className),
            children: "Only one version on file."
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: clsx('space-y-2', className),
        children: [
            title && !compact && /*#__PURE__*/ _jsx("div", {
                className: "text-[11px] font-semibold uppercase tracking-wide text-slate-500",
                children: title
            }),
            /*#__PURE__*/ _jsx("ol", {
                className: clsx('relative space-y-1.5', !compact && 'border-l border-slate-200 pl-3'),
                children: entries.map((entry)=>/*#__PURE__*/ _jsx(TimelineRow, {
                        entry: entry,
                        compact: compact
                    }, entry.key))
            })
        ]
    });
}
function TimelineRow({ entry, compact }) {
    return /*#__PURE__*/ _jsxs("li", {
        className: clsx('group relative flex items-start gap-2', // Small left-edge accent for the current version. Lives outside
        // the .pl-3 padding so it visually anchors against the parent
        // border line. Skipped in compact mode (no parent line to align to).
        !compact && entry.isCurrent && 'before:absolute before:-left-3 before:top-1.5 before:h-2 before:w-0.5 before:bg-emerald-500'),
        children: [
            /*#__PURE__*/ _jsx(AmendmentBadge, {
                amendmentNumber: entry.amendmentNumber,
                amendmentSequence: entry.amendmentSequence,
                isCurrent: entry.isCurrent,
                sourceFilename: entry.sourceFilename,
                size: compact ? 'xs' : 'sm'
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: clsx('truncate', entry.isCurrent ? 'text-sm font-medium text-slate-900' : 'text-sm text-slate-600', compact && 'text-xs'),
                        title: entry.primary,
                        children: entry.primary
                    }),
                    entry.secondary && !compact && /*#__PURE__*/ _jsx("div", {
                        className: "mt-0.5 line-clamp-2 text-xs text-slate-500",
                        children: entry.secondary
                    })
                ]
            })
        ]
    });
}
/**
 * formatTimelineSubtitle is a small helper for callers that want to render
 * a one-liner summary of a timeline (e.g. "25 → 30 pages, current AM003")
 * inline next to a row title without expanding the full timeline.
 *
 * Uses an arrow only when there's actual change between consecutive entries
 * (compared by their `primary` text). Renders the current version in bold
 * via the formatAmendmentLabel helper from AmendmentBadge.
 */ export function formatTimelineSubtitle(entries) {
    if (entries.length === 0) return '';
    if (entries.length === 1) {
        return entries[0].primary;
    }
    // Reverse so we render base → ... → current (most-readable order).
    const ordered = [
        ...entries
    ].reverse();
    const seen = [];
    for (const e of ordered){
        if (seen.length === 0 || seen[seen.length - 1] !== e.primary) {
            seen.push(e.primary);
        }
    }
    if (seen.length === 1) {
        // Same value across all amendments — no transition to show.
        const current = entries.find((e)=>e.isCurrent) ?? entries[0];
        return `${seen[0]} (${formatAmendmentLabel(current.amendmentNumber, current.amendmentSequence)})`;
    }
    return seen.join(' → ');
}
